import { useCallback, useMemo, useRef } from 'react';
import useNavigateWithSearch from '../../../../../hooks/useNavigateWithSearch';
import { BUTTON_SIZE, BUTTON_TYPE, Button } from '../../../global/Button';

import { cancelMembershipRenewalSubscription, getMembershipSubscription } from '../../../utils/service';

import { formatTransactionDate } from '../../../utils/dates';
import { formatMoney } from '../../../../../utils/formatMoney';
import { SUBSCRIPTION_STATUSES } from '../utils';
import { ROUTE_MEMBERSHIP } from '../../../config/routes';

import './CancelRenewal.scss';

const CancelRenewal = ({
  onHandleNext,
  membershipSubscriptionData,
  setMembershipSubscriptionData,
  setIsLoading,
  comments,
  reason,
  setError,
}) => {
  const navigateWithSearch = useNavigateWithSearch();
  const { uid: subscriptionId, stripeStatus, cancelAtPeriodEnd } = membershipSubscriptionData;
  const pollingTimeoutRef = useRef(null);

  const handleCleanup = useCallback(() => {
    setError(null);

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, [setError]);

  const handleCancelRenewal = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const cancellationComment = `${comments.description}, ${reason}`;
    const { error: cancelError } = await cancelMembershipRenewalSubscription({
      subscriptionId,
      cancelAtPeriodEnd: true,
      cancellationComment,
    });

    if (cancelError) {
      setError('Oops! Something went wrong. Please try again.');
      setIsLoading(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 500;

    const pollForUpdate = async () => {
      attempts++;

      const { data, error } = await getMembershipSubscription();

      if (error || !data?.uid) {
        setError('Error verifying the cancellation status. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      if (data?.cancelAtPeriodEnd === true) {
        setMembershipSubscriptionData({
          ...membershipSubscriptionData,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd,
        });
        setIsLoading(false);
        handleCleanup();
        onHandleNext();
        return;
      }

      if (attempts >= maxAttempts) {
        setError('The cancellation request was sent but we could not confirm the status. Please refresh the page to check.');
        setIsLoading(false);
        return;
      }

      pollingTimeoutRef.current = setTimeout(pollForUpdate, pollInterval);
    };

    pollingTimeoutRef.current = setTimeout(pollForUpdate, pollInterval);
  }, [
    subscriptionId,
    setMembershipSubscriptionData,
    comments,
    reason,
    setError,
    setIsLoading,
    onHandleNext,
    handleCleanup,
    membershipSubscriptionData,
  ]);

  const renewalStatus = useMemo(() => {
    if (stripeStatus === SUBSCRIPTION_STATUSES.ACTIVE && !cancelAtPeriodEnd) {
      return {
        showButton: true,
        message: `Your membership will renew on ${formatTransactionDate(membershipSubscriptionData.currentPeriodEnd)} for ${formatMoney(membershipSubscriptionData.amount)}`,
      };
    }

    if (stripeStatus === SUBSCRIPTION_STATUSES.ACTIVE && cancelAtPeriodEnd) {
      return {
        showButton: false,
        message: `Access until ${formatTransactionDate(membershipSubscriptionData.currentPeriodEnd)} · Will not renew`,
      };
    }

    return {
      showButton: false,
      message: 'Your membership is inactive',
    };
  }, [stripeStatus, cancelAtPeriodEnd, membershipSubscriptionData]);

  return (
    <div className="CancelRenewal">
      <div className="CancelRenewal__container">
        <div className="CancelRenewal__container--content">
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            onClick={() => navigateWithSearch(ROUTE_MEMBERSHIP)}
          >
            Keep Trashie Unlimited
          </Button>
          {renewalStatus.showButton && (
            <Button
              type={BUTTON_TYPE.LINK_QUATERNARY}
              size={BUTTON_SIZE.SMALL}
              className="CancelRenewal__container--content-cta"
              onClick={handleCancelRenewal}
            >
              Cancel Auto-Renewal
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelRenewal;
